import { usePostHog } from "posthog-js/react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type Context = Record<string, any>;

interface AnalyticsContextType {
  setContext: (context: Context) => void;
  clearContext: () => void;
  capture: (event: string, eventContext?: Context) => void;
  startTimeRef?: React.MutableRefObject<number | null>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

// posthog-js caps capture with a global token bucket (100 burst, 10/second), so N
// same-named components unmounting in one React commit must emit one duration
// event, not N. The window is keyed on the component name alone, with no notion
// of instance or commit: ANY unmount of that name within DURATION_COOLDOWN_MS of
// the last captured one is dropped, including a longer-lived component that
// merely shares the name (e.g. the `ask_puntt_chat` page unmounting just after
// one of its messages reports the message's duration, not the page's). That is
// the intended trade — the dropped captures from the flood cost more than the
// occasional duration lost to a shared name.
const DURATION_COOLDOWN_MS = 1000;
const lastDurationCaptureAt = new Map<string, number>();

//TODO: Add extended functionality for timing events
export const AnalyticsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const posthog = usePostHog();
  const [context, setContextState] = useState<Context>({});

  const setContext = (newContext: Context) => {
    setContextState((prevContext) => ({ ...prevContext, ...newContext }));
  };

  const clearContext = () => {
    setContextState({});
  };

  const capture = (event: string, eventContext?: Context) => {
    const mergedContext = { ...context, ...eventContext };
    posthog.capture(event, mergedContext);
  };

  return (
    <AnalyticsContext.Provider value={{ setContext, clearContext, capture }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (componentName?: string): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  const { setContext, clearContext, capture } = context;

  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    startTimeRef.current = performance.now();

    return () => {
      // `!= null`, not truthiness: `performance.now()` can legitimately read 0
      // (the time origin), and callers already treat 0 as a real reading.
      if (startTimeRef.current != null && componentName) {
        const now = performance.now();
        const lastCaptureAt = lastDurationCaptureAt.get(componentName);
        if (
          lastCaptureAt !== undefined &&
          now - lastCaptureAt < DURATION_COOLDOWN_MS
        ) {
          return;
        }
        lastDurationCaptureAt.set(componentName, now);
        capture(`${componentName}_duration`, {
          duration: now - startTimeRef.current,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentName]);

  return { setContext, clearContext, capture, startTimeRef };
};

export function errorAnalyticsPayload(error: unknown) {
  if (!(error instanceof Error)) {
    return {
      error_message: String(error),
      error_name: "Non-Error thrown",
      error_stack: "",
    };
  }

  return {
    error_message: typeof error.message === "undefined" ? "" : error.message,
    error_name: typeof error.name === "undefined" ? "" : error.name,
    error_stack: typeof error.stack === "undefined" ? "" : error.stack,
    ...(error.cause instanceof Error
      ? {
          error_cause_message:
            typeof error.cause.message === "undefined"
              ? ""
              : error.cause.message,
          error_cause_name:
            typeof error.cause.name === "undefined" ? "" : error.cause.name,
          error_cause_stack:
            typeof error.cause.stack === "undefined" ? "" : error.cause.stack,
        }
      : { error_cause: String(error.cause) }),
  };
}
